import { BUILD_INFO } from '../buildInfo';
import type { AppState, Actions } from '../state';
import type { DeliveryMode, Machine, Settings } from '../storage';

export function renderSettings(root: HTMLElement, state: AppState, actions: Actions): void {
  const s = state.settings;
  const isNocturne = s.deliveryMode === 'nocturne-tray';
  root.innerHTML = `
    <div class="screen screen-settings">
      <header class="topbar">
        <button class="btn btn-ghost" data-action="back">← Back</button>
        <h1>Settings</h1>
      </header>
      <section>
        <h2>Delivery</h2>
        <label class="field">
          <span>Mode</span>
          <select data-field="delivery-mode">
            <option value="download"${s.deliveryMode === 'download' ? ' selected' : ''}>Download to this device</option>
            <option value="nocturne-tray"${isNocturne ? ' selected' : ''}>Send to a Nocturne machine</option>
          </select>
        </label>
        <p class="hint">${
          isNocturne
            ? 'Notes are relayed through your Cloudflare worker to a machine running the Nocturne tray app.'
            : 'Each note saves as a .md file to this device. You move it where you want afterwards.'
        }</p>
      </section>
      <section>
        <h2>Vision model</h2>
        <label class="field">
          <span>Provider</span>
          <select data-field="provider">
            <option value="gemini"${s.provider === 'gemini' ? ' selected' : ''}>Google Gemini</option>
          </select>
        </label>
        <label class="field">
          <span>API key</span>
          <input type="password" data-field="apiKey" value="${esc(s.apiKey)}" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" placeholder="Paste your API key" />
        </label>
      </section>
      ${
        isNocturne
          ? `
      <section>
        <h2>Machines</h2>
        <div class="list" data-list="machines"></div>
        <button class="btn" data-action="add-machine">+ Add machine</button>
      </section>
      <section>
        <h2>Books</h2>
        <div class="list" data-list="books"></div>
        <button class="btn" data-action="add-book">+ Add book</button>
      </section>`
          : ''
      }
      <div class="build-info">v${esc(BUILD_INFO.version)} · build ${BUILD_INFO.buildNumber} · ${esc(new Date(BUILD_INFO.buildDate).toLocaleDateString())}</div>
    </div>
  `;

  const deliveryModeSelect = root.querySelector<HTMLSelectElement>('[data-field="delivery-mode"]')!;
  deliveryModeSelect.addEventListener('change', () => {
    updateSettings(state, actions, { deliveryMode: deliveryModeSelect.value as DeliveryMode });
    actions.rerender();
  });

  const providerSelect = root.querySelector<HTMLSelectElement>('[data-field="provider"]')!;
  providerSelect.addEventListener('change', () => {
    updateSettings(state, actions, { provider: 'gemini' });
  });

  const keyInput = root.querySelector<HTMLInputElement>('[data-field="apiKey"]')!;
  keyInput.addEventListener('input', () => {
    updateSettings(state, actions, { apiKey: keyInput.value.trim() });
  });

  root.querySelector<HTMLButtonElement>('[data-action="back"]')!.addEventListener('click', () => {
    actions.go('capture');
  });

  if (isNocturne) {
    renderMachines(root, state, actions);
    renderBooks(root, state, actions);

    root.querySelector<HTMLButtonElement>('[data-action="add-machine"]')!.addEventListener('click', () => {
      const next = {
        ...state.settings,
        machines: [
          ...state.settings.machines,
          { name: '', workerUrl: '', bearerToken: '' },
        ],
      };
      actions.updateSettings(next);
      actions.rerender();
    });

    root.querySelector<HTMLButtonElement>('[data-action="add-book"]')!.addEventListener('click', () => {
      const next = { ...state.settings, books: [...state.settings.books, ''] };
      actions.updateSettings(next);
      actions.rerender();
    });
  }
}

function renderMachines(root: HTMLElement, state: AppState, actions: Actions): void {
  const list = root.querySelector<HTMLElement>('[data-list="machines"]')!;
  list.innerHTML = '';
  state.settings.machines.forEach((m, index) => {
    const row = document.createElement('div');
    row.className = 'list-row';
    row.innerHTML = `
      <label class="field">
        <span>Name</span>
        <input data-row-field="name" value="${esc(m.name)}" autocapitalize="none" autocorrect="off" spellcheck="false" placeholder="linux-main" />
      </label>
      <label class="field">
        <span>Worker URL</span>
        <input type="url" data-row-field="workerUrl" value="${esc(m.workerUrl)}" autocapitalize="none" autocorrect="off" spellcheck="false" inputmode="url" placeholder="https://…workers.dev/relay" />
      </label>
      <label class="field">
        <span>Bearer token</span>
        <input type="password" data-row-field="bearerToken" value="${esc(m.bearerToken)}" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" />
      </label>
      <button class="btn btn-danger btn-small" data-row-action="remove">Remove</button>
    `;
    row.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
      input.addEventListener('input', () => {
        const field = input.getAttribute('data-row-field') as keyof Machine;
        const updated = { ...state.settings.machines[index]!, [field]: input.value.trim() };
        const machines = [...state.settings.machines];
        machines[index] = updated;
        updateSettings(state, actions, { machines });
      });
    });
    row.querySelector<HTMLButtonElement>('[data-row-action="remove"]')!.addEventListener('click', () => {
      const machines = state.settings.machines.filter((_, i) => i !== index);
      actions.updateSettings({ ...state.settings, machines });
      actions.rerender();
    });
    list.appendChild(row);
  });
}

function renderBooks(root: HTMLElement, state: AppState, actions: Actions): void {
  const list = root.querySelector<HTMLElement>('[data-list="books"]')!;
  list.innerHTML = '';
  state.settings.books.forEach((name, index) => {
    const row = document.createElement('div');
    row.className = 'list-row list-row-inline';
    row.innerHTML = `
      <label class="field">
        <span>Book name</span>
        <input data-row-field="name" value="${esc(name)}" autocapitalize="none" autocorrect="off" spellcheck="false" placeholder="fieldnotes" />
      </label>
      <button class="btn btn-danger btn-small" data-row-action="remove">Remove</button>
    `;
    const input = row.querySelector<HTMLInputElement>('input')!;
    input.addEventListener('input', () => {
      const books = [...state.settings.books];
      books[index] = input.value.trim();
      updateSettings(state, actions, { books });
    });
    row.querySelector<HTMLButtonElement>('[data-row-action="remove"]')!.addEventListener('click', () => {
      const books = state.settings.books.filter((_, i) => i !== index);
      actions.updateSettings({ ...state.settings, books });
      actions.rerender();
    });
    list.appendChild(row);
  });
}

function updateSettings(state: AppState, actions: Actions, patch: Partial<Settings>): void {
  actions.updateSettings({ ...state.settings, ...patch });
}

function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
